/*
 * newsbot - forked from autobrr
 */

import { Fragment, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from "@headlessui/react";
import {
  EllipsisHorizontalIcon,
  PencilSquareIcon,
  TrashIcon,
  EyeIcon,
  ClipboardDocumentIcon,
  PlusIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";

import { useToggle } from "@hooks/hooks";
import { classNames } from "@utils";
import { toast } from "@components/hot-toast";
import Toast from "@components/notifications/Toast";
import { DeleteModal } from "@components/modals";
import { EmptySimple } from "@components/emptystates";
import { Checkbox } from "@components/Checkbox";
import { CategoryAddForm } from "./settings/CategoryAddForm";
import { ExportUrlsPanel } from "./filters/ExportUrlsPanel";
import {
  useCategoriesWithStats,
  categoryKeys,
  useCategoryStats,
  useCreateCategory,
  useUpdateCategory,
} from "@api/queries";
import { api } from "@api/client";
import type { CategoryWithStats } from "@/types";

type FilterStatus = "all" | "enabled" | "disabled";
type SortOrder = "name" | "priority" | "updated" | "stories";

export function Filters() {
  const [filterStatus, setFilterStatus] = useState<FilterStatus>("all");
  const [sortOrder, setSortOrder] = useState<SortOrder>("priority");
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CategoryWithStats | null>(null);

  const { data: categories, isLoading } = useCategoriesWithStats();
  const { data: stats } = useCategoryStats();
  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategory(editingCategory?.id ?? "");

  // Count categories by status
  const enabledCount = categories?.filter((cat) => cat.enabled).length ?? 0;
  const disabledCount = categories?.filter((cat) => !cat.enabled).length ?? 0;

  const filteredCategories = categories
    ?.filter((cat) => {
      if (filterStatus === "enabled") return cat.enabled;
      if (filterStatus === "disabled") return !cat.enabled;
      return true;
    })
    ?.sort((a, b) => {
      switch (sortOrder) {
        case "name":
          return a.name.localeCompare(b.name);
        case "priority":
          return b.priority - a.priority; // Higher priority first
        case "stories":
          return b.story_count - a.story_count; // More stories first
        case "updated":
          return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
        default:
          return 0;
      }
    });

  const openCreateModal = () => {
    setEditingCategory(null);
    setFormModalOpen(true);
  };

  const openEditModal = (category: CategoryWithStats) => {
    setEditingCategory(category);
    setFormModalOpen(true);
  };

  const closeFormModal = () => {
    setFormModalOpen(false);
    setEditingCategory(null);
  };

  const handleFormSubmit = async (values: {
    name: string;
    slug: string;
    keywords: string;
    priority: number;
    enabled: boolean;
  }) => {
    const keywords = values.keywords
      .split(",")
      .map((k) => k.trim())
      .filter((k) => k.length > 0);

    try {
      if (editingCategory) {
        // If editing a system category, create a new one instead (override)
        if (editingCategory.user_id === null) {
          await createCategory.mutateAsync({ ...values, keywords });
          toast.custom((t) => (
            <Toast type="success" body={`Category "${values.name}" created (overrides system default)`} t={t} />
          ));
        } else {
          await updateCategory.mutateAsync({ ...values, keywords });
          toast.custom((t) => (
            <Toast type="success" body={`Category "${values.name}" updated`} t={t} />
          ));
        }
      } else {
        await createCategory.mutateAsync({ ...values, keywords });
        toast.custom((t) => (
          <Toast type="success" body={`Category "${values.name}" created`} t={t} />
        ));
      }
      closeFormModal();
    } catch (error) {
      toast.custom((t) => (
        <Toast type="error" body={`Failed to save category: ${String(error)}`} t={t} />
      ));
    }
  };

  return (
    <main>
      <div className="my-6 max-w-(--breakpoint-xl) mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-black dark:text-white">Filters</h1>
      </div>

      <div className="max-w-(--breakpoint-xl) mx-auto pb-6 px-2 sm:px-6 lg:pb-16 lg:px-8">
        {/* Form SlideOver */}
        <CategoryAddForm
          isOpen={formModalOpen}
          toggle={closeFormModal}
          category={editingCategory}
          onSubmit={handleFormSubmit}
          onDelete={editingCategory ? () => {
          } : undefined}
        />

        {/* Header with filters, sort, and Add button */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-4 flex-wrap">
            <FilterTabs
              value={filterStatus}
              onChange={setFilterStatus}
              counts={{ all: categories?.length ?? 0, enabled: enabledCount, disabled: disabledCount }}
            />
            <SortDropdown value={sortOrder} onChange={setSortOrder} />
          </div>
          <button
            type="button"
            onClick={openCreateModal}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <PlusIcon className="w-4 h-4" />
            Add Filter
          </button>
        </div>

        {/* Export URLs panel */}
        <ExportUrlsPanel />

        {/* Uncategorized count info */}
        {stats && stats.uncategorized_count > 0 && (
          <div className="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              {stats.uncategorized_count} uncategorized{" "}
              {stats.uncategorized_count === 1 ? "story" : "stories"}.
              Stories are automatically categorized every few minutes by keyword matching and AI.
            </p>
          </div>
        )}

        {/* Categories list */}
        {isLoading ? (
          <div className="bg-white dark:bg-gray-800 border border-gray-250 dark:border-gray-775 shadow-table rounded-md p-8">
            <div className="animate-pulse text-center text-gray-500">Loading categories...</div>
          </div>
        ) : filteredCategories && filteredCategories.length > 0 ? (
          <div className="bg-white dark:bg-gray-800 border border-gray-250 dark:border-gray-775 shadow-table rounded-md overflow-hidden">
            <ul className="min-w-full">
              <li className="hidden md:grid grid-cols-12 border-b border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wider bg-gray-50 dark:bg-gray-850">
                <div className="flex col-span-1 pl-4 py-3">Enabled</div>
                <div className="col-span-3 pl-4 py-3">Name</div>
                <div className="flex col-span-4 py-3">Keywords</div>
                <div className="flex col-span-2 py-3">Stories</div>
                <div className="flex col-span-1 py-3">Priority</div>
                <div className="col-span-1"></div>
              </li>
              {filteredCategories.map((category) => (
                <ListItem key={category.id} category={category} onEdit={openEditModal} />
              ))}
            </ul>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 border border-gray-250 dark:border-gray-775 shadow-table rounded-md">
            <EmptySimple title="No categories" subtitle="Add a category to get started" />
          </div>
        )}
      </div>
    </main>
  );
}

interface FilterTabsProps {
  value: FilterStatus;
  onChange: (value: FilterStatus) => void;
  counts: { all: number; enabled: number; disabled: number };
}

function FilterTabs({ value, onChange, counts }: FilterTabsProps) {
  const tabs: { key: FilterStatus; label: string; count: number }[] = [
    { key: "all", label: "All", count: counts.all },
    { key: "enabled", label: "Enabled", count: counts.enabled },
    { key: "disabled", label: "Disabled", count: counts.disabled },
  ];

  return (
    <div className="flex gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-md">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          onClick={() => onChange(tab.key)}
          className={classNames(
            "px-3 py-1 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5",
            value === tab.key
              ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
              : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          )}
        >
          {tab.label}
          <span
            className={classNames(
              "text-xs px-1.5 py-0.5 rounded-full",
              value === tab.key
                ? "bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300"
                : "bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
            )}
          >
            {tab.count}
          </span>
        </button>
      ))}
    </div>
  );
}

interface SortDropdownProps {
  value: SortOrder;
  onChange: (value: SortOrder) => void;
}

function SortDropdown({ value, onChange }: SortDropdownProps) {
  const options: { key: SortOrder; label: string }[] = [
    { key: "priority", label: "Priority" },
    { key: "name", label: "Name" },
    { key: "stories", label: "Story Count" },
    { key: "updated", label: "Recently Updated" },
  ];

  const currentLabel = options.find((o) => o.key === value)?.label ?? "Sort";

  return (
    <Menu as="div" className="relative">
      <MenuButton className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700">
        Sort: {currentLabel}
        <ChevronDownIcon className="w-4 h-4" />
      </MenuButton>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <MenuItems className="absolute left-0 mt-1 w-40 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-10">
          <div className="py-1">
            {options.map((option) => (
              <MenuItem key={option.key}>
                {({ active }) => (
                  <button
                    type="button"
                    onClick={() => onChange(option.key)}
                    className={classNames(
                      "w-full text-left px-3 py-2 text-sm",
                      active
                        ? "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white"
                        : "text-gray-700 dark:text-gray-300",
                      value === option.key && "font-medium text-blue-600 dark:text-blue-400"
                    )}
                  >
                    {option.label}
                  </button>
                )}
              </MenuItem>
            ))}
          </div>
        </MenuItems>
      </Transition>
    </Menu>
  );
}

interface ListItemProps {
  category: CategoryWithStats;
  onEdit: (category: CategoryWithStats) => void;
}

function ListItem({ category, onEdit }: ListItemProps) {
  const [enabled, setEnabled] = useState(category.enabled);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const updateMutation = useMutation({
    mutationFn: (status: boolean) =>
      api.patch(`/categories/${category.id}/enabled`, { enabled: status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
      queryClient.invalidateQueries({ queryKey: categoryKeys.withStats });
      toast.custom((t) => (
        <Toast
          type="success"
          body={`${category.name} was ${enabled ? "disabled" : "enabled"} successfully.`}
          t={t}
        />
      ));
    },
  });

  const toggleActive = (status: boolean) => {
    setEnabled(status);
    updateMutation.mutate(status);
  };

  const handleRowClick = () => {
    navigate({ to: "/filters/$filterId", params: { filterId: category.id } });
  };

  const keywordsPreview =
    category.keywords.length > 3
      ? `${category.keywords.slice(0, 3).join(", ")}...`
      : category.keywords.join(", ");

  return (
    <li>
      {/* Desktop: grid layout */}
      <div className="hidden md:grid grid-cols-12 items-center text-sm font-medium text-gray-900 dark:text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-750">
        <div className="col-span-1 pl-6 flex items-center" onClick={(e) => e.stopPropagation()}>
          <Checkbox value={enabled} setValue={toggleActive} />
        </div>
        <div
          className="col-span-3 pl-4 py-3 flex flex-col cursor-pointer"
          onClick={handleRowClick}
        >
          <div className="flex items-center gap-2">
            <span className="pr-2 dark:text-white truncate">{category.name}</span>
            {category.user_id === null && (
              <span className="px-1.5 py-0.5 text-xs rounded bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                System
              </span>
            )}
          </div>
          <span className="text-xs text-gray-500">{category.slug}</span>
        </div>
        <div
          className="flex col-span-4 py-3 items-center cursor-pointer"
          onClick={handleRowClick}
        >
          <span className="truncate text-xs text-gray-500" title={category.keywords.join(", ")}>
            {keywordsPreview || "No keywords"}
          </span>
        </div>
        <div
          className="flex col-span-2 py-3 items-center cursor-pointer"
          onClick={handleRowClick}
        >
          <span
            className={classNames(
              "px-2 py-0.5 rounded-full text-xs",
              category.story_count === 0
                ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300"
                : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
            )}
          >
            {category.story_count}
          </span>
        </div>
        <div
          className="flex col-span-1 py-3 items-center cursor-pointer"
          onClick={handleRowClick}
        >
          <span className="text-gray-500">{category.priority}</span>
        </div>
        <div className="col-span-1 flex justify-center items-center px-6" onClick={(e) => e.stopPropagation()}>
          <CategoryItemDropdown category={category} onEdit={() => onEdit(category)} />
        </div>
      </div>

      {/* Mobile: card layout */}
      <div className="flex md:hidden items-center gap-3 px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-750" onClick={handleRowClick}>
        <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
          <Checkbox value={enabled} setValue={toggleActive} />
        </div>
        <div className="flex-1 min-w-0 cursor-pointer">
          <span className="dark:text-white font-medium truncate block">{category.name}</span>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs text-gray-500 truncate">{category.slug}</span>
            {category.user_id === null && (
              <span className="shrink-0 px-1.5 py-0.5 text-xs rounded bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                System
              </span>
            )}
            <span
              className={classNames(
                "shrink-0 px-2 py-0.5 rounded-full text-xs",
                category.story_count === 0
                  ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300"
                  : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
              )}
            >
              {category.story_count}
            </span>
          </div>
        </div>
        <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
          <CategoryItemDropdown category={category} onEdit={() => onEdit(category)} />
        </div>
      </div>
    </li>
  );
}

interface CategoryItemDropdownProps {
  category: CategoryWithStats;
  onEdit: () => void;
}

const CategoryItemDropdown = ({ category, onEdit }: CategoryItemDropdownProps) => {
  const cancelModalButtonRef = useRef(null);
  const queryClient = useQueryClient();

  const [deleteModalIsOpen, toggleDeleteModal] = useToggle(false);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/categories/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
      queryClient.invalidateQueries({ queryKey: categoryKeys.withStats });
      toast.custom((t) => (
        <Toast type="success" body={`Category ${category.name} was deleted`} t={t} />
      ));
    },
  });

  return (
    <Menu as="div">
      <DeleteModal
        isOpen={deleteModalIsOpen}
        isLoading={deleteMutation.isPending}
        toggle={toggleDeleteModal}
        buttonRef={cancelModalButtonRef}
        deleteAction={() => {
          deleteMutation.mutate(category.id);
          toggleDeleteModal();
        }}
        title={`Remove category: ${category.name}`}
        text="Are you sure you want to remove this category? Stories in this category will be marked as uncategorized."
      />
      <MenuButton className="px-4 py-2">
        <EllipsisHorizontalIcon
          className="w-5 h-5 text-gray-700 hover:text-gray-900 dark:text-gray-100 dark:hover:text-gray-400"
          aria-hidden="true"
        />
      </MenuButton>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <MenuItems
          anchor={{ to: "bottom end", padding: "8px" }}
          className="absolute w-56 bg-white dark:bg-gray-825 divide-y divide-gray-200 dark:divide-gray-750 rounded-md shadow-lg border border-gray-250 dark:border-gray-750 focus:outline-hidden z-10"
        >
          <div className="px-1 py-1">
            <MenuItem>
              {({ active }) => (
                <button
                  className={classNames(
                    active ? "bg-blue-600 text-white" : "text-gray-900 dark:text-gray-300",
                    "font-medium group flex rounded-md items-center w-full px-2 py-2 text-sm"
                  )}
                  onClick={onEdit}
                >
                  <PencilSquareIcon
                    className={classNames(
                      active ? "text-white" : "text-blue-500",
                      "w-5 h-5 mr-2"
                    )}
                    aria-hidden="true"
                  />
                  {category.user_id === null ? "Customize" : "Edit"}
                </button>
              )}
            </MenuItem>
            <MenuItem>
              {({ active }) => (
                <a
                  href={`/stories?category=${category.slug}`}
                  className={classNames(
                    active ? "bg-blue-600 text-white" : "text-gray-900 dark:text-gray-300",
                    "font-medium group flex rounded-md items-center w-full px-2 py-2 text-sm"
                  )}
                >
                  <EyeIcon
                    className={classNames(
                      active ? "text-white" : "text-blue-500",
                      "w-5 h-5 mr-2"
                    )}
                    aria-hidden="true"
                  />
                  View Stories
                </a>
              )}
            </MenuItem>
            <MenuItem>
              {({ active }) => (
                <button
                  className={classNames(
                    active ? "bg-blue-600 text-white" : "text-gray-900 dark:text-gray-300",
                    "font-medium group flex rounded-md items-center w-full px-2 py-2 text-sm"
                  )}
                  onClick={() => {
                    const apiUrl = `${window.location.origin}/api/v1/categories/${category.id}`;
                    navigator.clipboard.writeText(apiUrl);
                    toast.custom((t) => (
                      <Toast type="success" body="API URL copied to clipboard" t={t} />
                    ));
                  }}
                >
                  <ClipboardDocumentIcon
                    className={classNames(
                      active ? "text-white" : "text-blue-500",
                      "w-5 h-5 mr-2"
                    )}
                    aria-hidden="true"
                  />
                  Copy API URL
                </button>
              )}
            </MenuItem>
          </div>
          {/* Only show delete for user categories (not system defaults) */}
          {category.user_id !== null && (
            <div className="px-1 py-1">
              <MenuItem>
                {({ active }) => (
                  <button
                    className={classNames(
                      active ? "bg-red-600 text-white" : "text-gray-900 dark:text-gray-300",
                      "font-medium group flex rounded-md items-center w-full px-2 py-2 text-sm"
                    )}
                    onClick={() => toggleDeleteModal()}
                  >
                    <TrashIcon
                      className={classNames(
                        active ? "text-white" : "text-red-500",
                        "w-5 h-5 mr-2"
                      )}
                      aria-hidden="true"
                    />
                    Delete
                  </button>
                )}
              </MenuItem>
            </div>
          )}
        </MenuItems>
      </Transition>
    </Menu>
  );
};
